import { children, find } from './fmp4-split';

/** AV1 §5.5 and AV1-ISOBMFF §2.3/§5. Read the encoded header, not requested settings. */
export interface Av1SequenceConfig {
  record: Uint8Array;
  sequence: Uint8Array;
  codec: string;
  width: number;
  height: number;
}
class Bits {
  private at = 0;
  constructor(private readonly bytes: Uint8Array) {}
  read(n: number): number {
    if (n > 32 || n < 0 || this.at + n > this.bytes.length * 8) throw new Error('AV1 truncated sequence header');
    let value = 0;
    for (let i = 0; i < n; i++, this.at++) value = value * 2 + ((this.bytes[this.at >> 3]! >> (7 - (this.at & 7))) & 1);
    return value;
  }
  uvlc(): void {
    let zeros = 0;
    while (zeros < 32 && this.read(1) === 0) zeros++;
    if (zeros < 32) this.read(zeros);
  }
}

export function av1SequenceConfig(chunk: Uint8Array): Av1SequenceConfig | null {
  for (let at = 0; at < chunk.length;) {
    const start = at, header = chunk[at++]!;
    if ((header & 0x81) || !(header & 2)) throw new Error('AV1 invalid low-overhead OBU header');
    if (header & 4) {
      if (at >= chunk.length || (chunk[at++]! & 7)) throw new Error('AV1 invalid OBU extension');
    }
    let length = 0, terminated = false;
    for (let i = 0; i < 8; i++) {
      if (at >= chunk.length) throw new Error('AV1 truncated OBU size');
      const b = chunk[at++]!;
      length += (b & 127) * 2 ** (i * 7);
      if (!(b & 128)) { terminated = true; break; }
    }
    if (!terminated || !Number.isSafeInteger(length) || length > chunk.length - at) throw new Error('AV1 invalid OBU size');
    const end = at + length;
    if (((header >> 3) & 15) !== 1) { at = end; continue; }
    const bits = new Bits(chunk.subarray(at, end));
    const profile = bits.read(3);
    if (profile > 2) throw new Error('AV1 unsupported profile');
    bits.read(1); // still_picture
    const reduced = bits.read(1);
    let level = 0, tier = 0, delayBits = 0, decoderModel = 0;
    if (reduced) level = bits.read(5);
    else {
      if (bits.read(1)) {
        bits.read(32); bits.read(32);
        if (bits.read(1)) bits.uvlc();
        decoderModel = bits.read(1);
        if (decoderModel) { delayBits = bits.read(5) + 1; bits.read(32); bits.read(5); bits.read(5); }
      }
      const initialDelay = bits.read(1), points = bits.read(5) + 1;
      for (let i = 0; i < points; i++) {
        bits.read(12);
        const l = bits.read(5), t = l > 7 ? bits.read(1) : 0;
        if (!i) { level = l; tier = t; }
        if (decoderModel && bits.read(1)) { bits.read(delayBits); bits.read(delayBits); bits.read(1); }
        if (initialDelay && bits.read(1)) bits.read(4);
      }
    }
    const widthBits = bits.read(4) + 1, heightBits = bits.read(4) + 1;
    const width = bits.read(widthBits) + 1, height = bits.read(heightBits) + 1;
    if (!reduced && bits.read(1)) { bits.read(4); bits.read(3); }
    bits.read(3); // superblock/filter/intra-edge
    if (!reduced) {
      bits.read(4);
      const orderHint = bits.read(1);
      if (orderHint) bits.read(2);
      const screenTools = bits.read(1) ? 2 : bits.read(1);
      if (screenTools && !bits.read(1)) bits.read(1);
      if (orderHint) bits.read(3);
    }
    bits.read(3); // superres/cdef/restoration
    const high = bits.read(1), twelve = profile === 2 && high ? bits.read(1) : 0;
    const depth = twelve ? 12 : high ? 10 : 8, mono = profile === 1 ? 0 : bits.read(1);
    let cp = 2, tc = 2, mc = 2;
    if (bits.read(1)) { cp = bits.read(8); tc = bits.read(8); mc = bits.read(8); }
    let full = 0, x = 1, y = 1, position = 0;
    if (mono) full = bits.read(1);
    else {
      if (cp === 1 && tc === 13 && mc === 0) { full = 1; x = y = 0; }
      else {
        full = bits.read(1);
        if (profile === 1) x = y = 0;
        else if (profile === 2) { x = depth === 12 ? bits.read(1) : 1; y = depth === 12 && x ? bits.read(1) : 0; }
        if (x && y) position = bits.read(2);
      }
      bits.read(1); // separate_uv_delta_q
    }
    bits.read(1); // film_grain_params_present
    const two = (value: number) => String(value).padStart(2, '0');
    return {
      record: new Uint8Array([0x81, (profile << 5) | level, (tier << 7) | (high << 6) | (twelve << 5) | (mono << 4) | (x << 3) | (y << 2) | position, 0]),
      sequence: chunk.slice(start, end), width, height,
      codec: `av01.${profile}.${two(level)}${tier ? 'H' : 'M'}.${two(depth)}.${mono}.${x}${y}${position}.${two(cp)}.${two(tc)}.${two(mc)}.${full}`,
    };
  }
  return null;
}

/** mp4-muxer 5.2 emits a four-byte placeholder. Replace through the public MP4 bytes. */
export function installAv1Config(init: Uint8Array, config: Av1SequenceConfig): void {
  const moov = find(init, 0, init.length, ['moov']);
  if (!moov) throw new Error('AV1 init missing moov');
  let installed = 0;
  for (const track of children(init, moov.body, moov.end).filter(b => b.type === 'trak')) {
    const stsd = find(init, track.body, track.end, ['mdia', 'minf', 'stbl', 'stsd']);
    if (!stsd) continue;
    for (const entry of children(init, stsd.body + 8, stsd.end).filter(b => b.type === 'av01')) {
      const record = find(init, entry.body + 78, entry.end, ['av1C']);
      if (!record || record.end - record.body !== 4) throw new Error('AV1 unexpected configuration box');
      init.set(config.record, record.body); installed++;
    }
  }
  if (installed !== 1) throw new Error('AV1 init requires exactly one AV1 track');
}
